import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import {
  CompareHeader,
  SavingsBanner,
  UnitsControl,
} from "./pricing-calc-shell";
import type { CalculatorState } from "./pricing-calculator";
import { LeftCardBody, RightCardBody } from "./pricing-compare-cards";
import { CostCards } from "./pricing-cost-cards";
import { MobileHeading, MobileHero } from "./pricing-mobile-hero";
import { MobileIncluded } from "./pricing-mobile-included";
import type { InfoId } from "./pricing-popovers";
import { HEADING_RICH_TAGS, T, hiddenCostsLead } from "./pricing-shared";

/** Phone/tablet pricing layout (single column, max 560px), composed from section parts. */
export function PricingMobile({
  calc,
  openInfo,
  onToggle,
  onClose,
}: {
  readonly calc: CalculatorState;
  readonly openInfo: InfoId | null;
  readonly onToggle: (id: InfoId) => void;
  readonly onClose: () => void;
}): ReactNode {
  const tPricingShared = useTranslations("public.pricingShared");
  const t = useTranslations("public.pricingMobile");
  return (
    <div
      style={{
        width: "100%",
        maxWidth: 560,
        margin: "0 auto",
        background: "#FFFFFF",
        overflow: "hidden",
        color: T.ink,
        fontFamily: T.sans,
      }}
    >
      <MobileHero />
      <MobileIncluded />

      <div
        data-screen-label={t("verbrauch")}
        style={{
          borderTop: "1px solid rgba(11,15,25,0.10)",
          padding: "44px 20px 56px",
        }}
      >
        <MobileHeading
          title={t.rich("keineVerstecktenKosten", HEADING_RICH_TAGS)}
          lead={hiddenCostsLead(tPricingShared)}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <CostCards
            compact
            openInfo={openInfo}
            onToggle={onToggle}
            onClose={onClose}
          />
        </div>
      </div>

      <div
        id="spar-rechner"
        data-screen-label={t("rechner")}
        style={{
          borderTop: "1px solid rgba(11,15,25,0.10)",
          padding: "44px 20px 64px",
        }}
      >
        <MobileHeading
          title={t.rich("rechneDeinenVorteilAus", HEADING_RICH_TAGS)}
          lead={
            <>
              {t("tragHeutigenToolKosten")}{" "}
              <strong style={{ color: T.ink, fontWeight: 600 }}>
                {t("alleBetraegeProMonat")}
              </strong>
            </>
          }
        />
        <UnitsControl calc={calc} compact />
        <SavingsBanner calc={calc} compact />

        <div
          style={{
            border: "1px solid rgba(11,15,25,0.12)",
            borderRadius: 16,
            background: "#fff",
            marginBottom: 16,
          }}
        >
          <CompareHeader
            eyebrow={t("hausverwalterAi")}
            eyebrowColor="#9AA0A6"
            title={t("heutigenTools")}
            total={calc.leftTotalLabel}
            totalUnit={t("monat")}
            totalUnitColor="#8A8D94"
            yearLabel={calc.leftYearLabel}
            compact
          />
          <div style={{ padding: "0 18px 14px" }}>
            <LeftCardBody
              calc={calc}
              openInfo={openInfo}
              onToggle={onToggle}
              onClose={onClose}
            />
          </div>
        </div>

        <div
          style={{
            position: "relative",
            border: "1px solid rgba(0,128,112,0.25)",
            borderRadius: 16,
            background: "linear-gradient(180deg, #FAFAFA 0%, #ffffff 55%)",
            overflow: "hidden",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              height: 3,
              background: T.accent,
            }}
          />
          <CompareHeader
            eyebrow={t("hausverwalterAi2")}
            eyebrowColor={T.accent}
            title={t("systemPreis")}
            total={calc.rightTotalLabel}
            totalUnit={t("monat")}
            totalUnitColor={T.accent}
            yearLabel={calc.rightYearLabel}
            compact
          />
          <div style={{ padding: "2px 18px 18px" }}>
            <RightCardBody calc={calc} compact />
          </div>
        </div>

        <p
          style={{
            fontFamily: T.sans,
            fontSize: 12.5,
            lineHeight: 1.5,
            color: "#8A8D94",
            margin: "16px 2px 0",
            textAlign: "center",
          }}
        >
          {t("richtwerteEchtenHausverwaltungenBasis")}
        </p>
      </div>
    </div>
  );
}
